import React, { useEffect, useMemo, useState } from 'react'
import { Button, Close, Input, InputModal, StatusLabel } from '../../lib/components'
import {
  ALLOCATION_REQUEST_RECEIVED_BODY,
  ALLOCATION_REQUEST_RECEIVED_TITLE,
  ERROR,
  REQUEST_ALLOCATION,
  WITHDRAW_ALLOCATION_REQUEST,
} from '../../lib/utils/message'
import { convertNumberWithCommas, convertToNumber } from '../../lib/utils/helpers'
import './request-allocation-modal.scss'

export type RequestAllocationModalStep = 'form' | 'success'

interface RequestAllocationModalProps {
  opportunityName: string
  /** Opportunity minimum amount (ticket size) in cents. */
  minimumAmountCents?: number | null
  /** Prefill from existing interest (cents). */
  initialAmountCents?: number | null
  step: RequestAllocationModalStep
  submitting?: boolean
  deleting?: boolean
  onClose: () => void
  onSubmit: (amountCents: number) => void
  onDelete?: () => void
}

const ThumbsUpIcon = () => (
  <svg
    className='request-allocation-modal__success-icon'
    width='72'
    height='72'
    viewBox='0 0 72 72'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    aria-hidden
  >
    <rect x='8' y='8' width='56' height='56' rx='12' stroke='#0F4F99' strokeWidth='3' />
    <path
      d='M30.5 46.5V28.5C30.5 27.4 31.4 26.5 32.5 26.5H35.5C37.7 26.5 39.5 28.3 39.5 30.5V32.5H44.5C46.7 32.5 48.5 34.3 48.5 36.5V38C48.5 38.8 48.2 39.5 47.7 40L44.5 46.5C44 47.4 43 48 41.9 48H33.5C31.8 48 30.5 46.7 30.5 45V46.5ZM26.5 46.5H22.5C21.4 46.5 20.5 45.6 20.5 44.5V34.5C20.5 33.4 21.4 32.5 22.5 32.5H26.5V46.5Z'
      stroke='#0F4F99'
      strokeWidth='2.5'
      strokeLinejoin='round'
    />
  </svg>
)

const centsToDisplayDollars = (cents?: number | null): string => {
  if (!cents || cents <= 0) return ''
  const dollars = cents / 100
  return convertNumberWithCommas(dollars.toFixed(2))
}

const formatMinimumDollarsLabel = (minimumAmountCents: number): string => {
  const dollars = minimumAmountCents / 100
  const isWholeDollars = Number.isInteger(dollars)
  return convertNumberWithCommas(isWholeDollars ? dollars.toString() : dollars.toFixed(2))
}

const RequestAllocationModal = ({
  opportunityName,
  minimumAmountCents,
  initialAmountCents,
  step,
  submitting = false,
  deleting = false,
  onClose,
  onSubmit,
  onDelete,
}: RequestAllocationModalProps) => {
  const [amountDisplay, setAmountDisplay] = useState(centsToDisplayDollars(initialAmountCents))
  const [error, setError] = useState('')
  const isBusy = submitting || deleting
  const canWithdraw = Boolean(initialAmountCents && initialAmountCents > 0 && onDelete)
  const resolvedMinimumCents =
    typeof minimumAmountCents === 'number' &&
    Number.isFinite(minimumAmountCents) &&
    minimumAmountCents > 0
      ? minimumAmountCents
      : 0
  const minimumDollarsLabel = useMemo(
    () => (resolvedMinimumCents > 0 ? formatMinimumDollarsLabel(resolvedMinimumCents) : null),
    [resolvedMinimumCents],
  )

  useEffect(() => {
    setAmountDisplay(centsToDisplayDollars(initialAmountCents))
    setError('')
  }, [initialAmountCents, step])

  const handleAmountChange = (value: string) => {
    const numeric = convertToNumber(value)
    setAmountDisplay(numeric > 0 ? convertNumberWithCommas(numeric.toString()) : value)
    setError('')
  }

  const handleSubmit = () => {
    const dollars = convertToNumber(amountDisplay)
    const amountCents = Math.round(dollars * 100)
    if (!Number.isFinite(amountCents) || amountCents <= 0) {
      setError(
        resolvedMinimumCents > 0
          ? `Minimum reservation is $${minimumDollarsLabel}`
          : 'Enter a valid allocation amount',
      )
      return
    }
    if (resolvedMinimumCents > 0 && amountCents < resolvedMinimumCents) {
      setError(`Minimum reservation is $${minimumDollarsLabel}`)
      return
    }
    onSubmit(amountCents)
  }

  return (
    <InputModal testId='request-allocation-modal' onBackdropClick={onClose}>
      <div
        className='request-allocation-modal'
        role='dialog'
        aria-modal='true'
        aria-labelledby='request-allocation-modal-title'
      >
        <div
          className={`request-allocation-modal__progress ${
            step === 'success' ? 'request-allocation-modal__progress--complete' : ''
          }`}
          aria-hidden
        />
        <button
          type='button'
          className='request-allocation-modal__close'
          aria-label='Close'
          onClick={onClose}
        >
          <Close width='20px' height='20px' />
        </button>

        {step === 'form' ? (
          <>
            <p id='request-allocation-modal-title' className='request-allocation-modal__eyebrow'>
              {REQUEST_ALLOCATION}
            </p>
            <h2 className='request-allocation-modal__heading'>
              Indicate your interest in <strong>{opportunityName}</strong>
            </h2>

            <div className='request-allocation-modal__field-label'>Investment Size</div>
            <div
              className={`request-allocation-modal__money-input${
                error ? ' request-allocation-modal__money-input--error' : ''
              }`}
            >
              <span className='request-allocation-modal__money-prefix' aria-hidden>
                $
              </span>
              <Input
                type='text'
                name='allocationAmount'
                placeholder='0.00'
                value={amountDisplay}
                handleChange={(e) => handleAmountChange(e.target.value)}
                dataTestId='request-allocation-amount-input'
              />
            </div>
            {error ? (
              <div
                className='request-allocation-modal__error'
                data-testid='request-allocation-amount-error'
              >
                <StatusLabel type={ERROR} variant='basic' text={error} />
              </div>
            ) : null}
            {minimumDollarsLabel ? (
              <p className='request-allocation-modal__hint'>
                Minimum reservation: <strong>${minimumDollarsLabel}</strong>
              </p>
            ) : null}

            <div className='request-allocation-modal__actions'>
              <Button
                text={submitting ? 'Submitting…' : 'Submit'}
                variant='primary-btn'
                isDisabled={isBusy}
                testId='request-allocation-submit'
                handleClick={handleSubmit}
              />
              {canWithdraw ? (
                <Button
                  text={deleting ? 'Withdrawing…' : WITHDRAW_ALLOCATION_REQUEST}
                  variant='basic-btn'
                  classes={['request-allocation-modal__withdraw']}
                  isDisabled={isBusy}
                  testId='request-allocation-withdraw'
                  handleClick={() => onDelete?.()}
                />
              ) : null}
            </div>
          </>
        ) : (
          <div
            className='request-allocation-modal__success'
            data-testid='request-allocation-success'
          >
            <ThumbsUpIcon />
            <h2
              id='request-allocation-modal-title'
              className='request-allocation-modal__success-title'
            >
              {ALLOCATION_REQUEST_RECEIVED_TITLE}
            </h2>
            <p className='request-allocation-modal__success-body'>
              {ALLOCATION_REQUEST_RECEIVED_BODY}
            </p>
          </div>
        )}
      </div>
    </InputModal>
  )
}

export default RequestAllocationModal
